import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiClock, FiMapPin, FiCheck, FiX, FiActivity, FiSearch, FiLayout } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';
import toast from 'react-hot-toast';

const AdminSchedules = () => {
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInstModal, setShowInstModal] = useState(false);
    const [showSchedModal, setShowSchedModal] = useState(false);
    const [editingInst, setEditingInst] = useState(null);
    const [editingSched, setEditingSched] = useState(null);
    const [selectedInstId, setSelectedInstId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [instForm, setInstForm] = useState({
        name: '',
        location: '',
        description: '',
        logo_url: ''
    });

    const [schedForm, setSchedForm] = useState({
        class_name: '',
        day: '',
        time_slot: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const data = await fetchAPI('/get_schedules.php');
            setInstitutions(data);
        } catch (error) {
            toast.error('Failed to fetch schedules');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveInst = async (e) => {
        e.preventDefault();
        try {
            await fetchAPI('/admin/save_institution.php', {
                method: 'POST',
                body: JSON.stringify(instForm)
            });
            toast.success(instForm.id ? 'Institution updated' : 'Institution created');
            setShowInstModal(false);
            fetchData();
        } catch (error) {
            toast.error(error.message || 'Error saving institution');
        }
    };

    const handleDeleteInst = async (id) => {
        if (!window.confirm('Are you sure you want to delete this institution and all its schedules?')) return;
        try {
            await fetchAPI('/admin/delete_institution.php', {
                method: 'POST',
                body: JSON.stringify({ id })
            });
            toast.success('Institution deleted');
            fetchData();
        } catch (error) {
            toast.error(error.message || 'Error deleting institution');
        }
    };

    const handleSaveSched = async (e) => {
        e.preventDefault();
        try {
            const data = { ...schedForm, institution_id: selectedInstId };
            await fetchAPI('/admin/save_schedule.php', {
                method: 'POST',
                body: JSON.stringify(data)
            });
            toast.success(schedForm.id ? 'Schedule updated' : 'Schedule created');
            setShowSchedModal(false);
            fetchData();
        } catch (error) {
            toast.error(error.message || 'Error saving schedule');
        }
    };

    const handleDeleteSched = async (id) => {
        if (!window.confirm('Delete this schedule entry?')) return;
        try {
            await fetchAPI('/admin/delete_schedule.php', {
                method: 'POST',
                body: JSON.stringify({ id })
            });
            toast.success('Schedule deleted');
            fetchData();
        } catch (error) {
            toast.error(error.message || 'Error deleting schedule');
        }
    };

    const filteredInstitutions = institutions.filter(inst =>
        inst.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inst.location.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-4 md:p-8 space-y-8 bg-gray-50 min-h-screen">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight">SCHEDULE REGISTRY</h1>
                    <p className="text-gray-500 font-medium">Manage academic centers and class time tables</p>
                </div>
                <button
                    onClick={() => {
                        setInstForm({ name: '', location: '', description: '', logo_url: '' });
                        setShowInstModal(true);
                    }}
                    className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20"
                >
                    <FiPlus /> New Institution
                </button>
            </div>

            <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search centers or locations..."
                    className="w-full pl-12 pr-6 py-4 bg-white border border-gray-100 rounded-3xl shadow-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all font-medium"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {loading ? (
                <div className="flex justify-center py-20">
                    <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {filteredInstitutions.map((inst) => (
                        <motion.div
                            layout
                            key={inst.id}
                            className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden hover:shadow-xl transition-all group"
                        >
                            <div className="p-8 flex items-start gap-6 border-b border-gray-50">
                                <div className="w-20 h-20 bg-gray-50 rounded-2xl flex items-center justify-center p-3 ring-1 ring-gray-100 group-hover:bg-white transition-all">
                                    <img src={inst.logo_url} alt={inst.name} className="max-w-full max-h-full object-contain filter drop-shadow-sm" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="text-xl font-black text-gray-900 uppercase tracking-tight leading-none mb-1">{inst.name}</h3>
                                            <p className="text-gray-400 text-xs font-bold uppercase tracking-widest flex items-center gap-1">
                                                <FiMapPin size={10} className="text-blue-500" /> {inst.location}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    setInstForm(inst);
                                                    setShowInstModal(true);
                                                }}
                                                className="p-3 bg-gray-50 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                                            >
                                                <FiEdit2 size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteInst(inst.id)}
                                                className="p-3 bg-gray-50 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                                            >
                                                <FiTrash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-8">
                                <div className="flex justify-between items-center mb-6">
                                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                        <FiClock className="text-blue-500" /> Time Table Registry
                                    </h4>
                                    <button
                                        onClick={() => {
                                            setSelectedInstId(inst.id);
                                            setSchedForm({ class_name: '', day: '', time_slot: '' });
                                            setShowSchedModal(true);
                                        }}
                                        className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:text-blue-700 transition-colors flex items-center gap-1"
                                    >
                                        <FiPlus size={12} /> Add Entry
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {inst.schedules && inst.schedules.length > 0 ? (
                                        inst.schedules.map((sched) => (
                                            <div key={sched.id} className="bg-gray-50/50 rounded-2xl p-4 flex items-center justify-between group/row hover:bg-white border border-transparent hover:border-gray-100 transition-all">
                                                <div className="flex items-center gap-4">
                                                    <div className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg text-[10px] font-black uppercase tracking-tight">
                                                        {sched.class_name}
                                                    </div>
                                                    <div>
                                                        <span className="text-xs font-black text-gray-900 block leading-none mb-1">{sched.day}</span>
                                                        <span className="text-[10px] font-bold text-gray-400 block uppercase">{sched.time_slot}</span>
                                                    </div>
                                                </div>
                                                <div className="flex gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedInstId(inst.id);
                                                            setSchedForm(sched);
                                                            setShowSchedModal(true);
                                                        }}
                                                        className="p-2 text-gray-400 hover:text-blue-600 rounded-lg transition-colors"
                                                    >
                                                        <FiEdit2 size={12} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteSched(sched.id)}
                                                        className="p-2 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
                                                    >
                                                        <FiTrash2 size={12} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="py-6 text-center border-2 border-dashed border-gray-100 rounded-3xl">
                                            <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">No entries registered</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Institution Modal */}
            <AnimatePresence>
                {showInstModal && (
                    <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowInstModal(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="relative bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl p-10 overflow-hidden">
                            <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight mb-8">
                                {instForm.id ? 'Modify Center' : 'New Center'}
                            </h2>
                            <form onSubmit={handleSaveInst} className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Center Name</label>
                                        <input required className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-medium" value={instForm.name} onChange={e => setInstForm({ ...instForm, name: e.target.value })} />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Location</label>
                                        <input required className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-medium" value={instForm.location} onChange={e => setInstForm({ ...instForm, location: e.target.value })} />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Logo URL (Icon)</label>
                                    <input placeholder="/logos/example.png" className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-medium" value={instForm.logo_url} onChange={e => setInstForm({ ...instForm, logo_url: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Short Description</label>
                                    <textarea rows="3" className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-medium resize-none" value={instForm.description} onChange={e => setInstForm({ ...instForm, description: e.target.value })} />
                                </div>
                                <div className="flex gap-4 pt-4">
                                    <button type="button" onClick={() => setShowInstModal(false)} className="flex-1 py-4 bg-gray-100 text-gray-600 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-200 transition-all">Cancel</button>
                                    <button type="submit" className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20">Archive Changes</button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Schedule Modal */}
            <AnimatePresence>
                {showSchedModal && (
                    <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowSchedModal(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="relative bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl p-10 overflow-hidden">
                            <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight mb-8">
                                {schedForm.id ? 'Modify Entry' : 'New Entry'}
                            </h2>
                            <form onSubmit={handleSaveSched} className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Class (Grade/Type)</label>
                                    <input required placeholder="Grade 11 Theory" className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-medium" value={schedForm.class_name} onChange={e => setSchedForm({ ...schedForm, class_name: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Day</label>
                                    <input required placeholder="Tuesday" className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-medium" value={schedForm.day} onChange={e => setSchedForm({ ...schedForm, day: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Time Slot</label>
                                    <input required placeholder="03:30 PM - 05:30 PM" className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-medium" value={schedForm.time_slot} onChange={e => setSchedForm({ ...schedForm, time_slot: e.target.value })} />
                                </div>
                                <div className="flex gap-4 pt-4">
                                    <button type="button" onClick={() => setShowSchedModal(false)} className="flex-1 py-4 bg-gray-100 text-gray-600 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-200 transition-all">Cancel</button>
                                    <button type="submit" className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20">Archive Changes</button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminSchedules;
